import { getAuth, onAuthStateChanged, type Unsubscribe } from 'firebase/auth'
import { ref, onMounted, onUnmounted, type Ref } from 'vue'

/**
 * @description Reactive Firebase auth presence flag for components that only need to know
 * "is anyone signed in?" — not who. Subscribes to `onAuthStateChanged` on mount and
 * unsubscribes on unmount, so callers do not have to manage the listener lifecycle.
 *
 * Prefer `useAuthStore` when you need the actual `firebaseUser` / `dbUser` objects;
 * use this composable only for lightweight UI gating (e.g. show/hide a sign-in button).
 *
 * @returns `{ isSignedIn }` — a `Ref<boolean>` that flips to `true` while a Firebase user is
 * authenticated and back to `false` after sign-out.
 *
 * @example
 * const { isSignedIn } = useAuthState()
 * // template: <button v-if="!isSignedIn">Sign in</button>
 */
export function useAuthState(): { isSignedIn: Ref<boolean> } {
  const isSignedIn = ref(false)
  let unsubscribeAuth: Unsubscribe | undefined

  onMounted(() => {
    const auth = getAuth()
    unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      isSignedIn.value = !!user
    })
  })

  onUnmounted(() => {
    unsubscribeAuth?.()
  })

  return { isSignedIn }
}
